import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/free-solid-svg-icons";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Contexts
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Components
import Modal from "../../components/Modal.js";
import Help from "../../components/Help.js";
import SearchSelect from "../../components/SearchSelect.js";
import {InputError} from "../../components/form/FormInputs.js";

// Style
import {pad, radius} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {flex} from "../../style/components/mixins.js";
import {
  Form,
  FormGroup,
  FormField,
  ButtonFull,
  HeadingCenter,
  ButtonLoader,
  Inline,
  Text
} from "../../style/components/general.js";

const schema = yup.object().shape({
  tags: yup
    .array()
    .of(yup.object())
    .test({
      test: val => Array.isArray(val) && val.length > 0,
      message: "Please provide at least one tag."
    })
});

const normalizeTag = tag => {
  const label = tag?.label || tag?.name || tag;
  return {name: label, label};
};

const tagKey = tag => (tag?.label || tag?.name || "").toLowerCase();

const tagsMatch = (a, b) => {
  if (a.length !== b.length) return false;
  const keys = new Set(a.map(tagKey));
  return b.every(tag => keys.has(tagKey(tag)));
};

const getInitialTags = rows => {
  if (!rows?.length) return {tags: [], tagsDiffer: false};

  const perRow = rows.map(row => (row.tags || []).map(normalizeTag));
  const first = perRow[0];
  const tagsDiffer = perRow.some(tags => !tagsMatch(first, tags));

  if (!tagsDiffer) return {tags: first, tagsDiffer: false};

  const shared = first.filter(tag =>
    perRow.every(tags => tags.some(other => tagKey(other) === tagKey(tag)))
  );
  return {tags: shared, tagsDiffer: true};
};

const ModalBulkTagUpdate = ({visible, setVisible, facilityId, selected, updateTable}) => {
  const isMounted = useMountedState();

  const {currentUser} = useContext(AuthContext);

  const {api} = useApi("custom-table-rows");
  const {api: apiTags} = useApi("tags");

  const [allTags, setAllTags] = useState([]);
  const [tagResults, setTagResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const {tags: initialTags, tagsDiffer} = useMemo(() => getInitialTags(selected), [selected]);

  const form = useForm({
    defaultValues: {tags: initialTags},
    resolver: yupResolver(schema)
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: {errors}
  } = form;

  const {append: appendTag} = useFieldArray({control, name: "tags"});
  const selectedTags = watch("tags");

  useEffect(() => {
    reset({tags: initialTags});
  }, [initialTags, reset]);

  useEffect(() => {
    if (isMounted()) {
      apiTags.callGet().then(({status, data}) => {
        if (status === 200 && data)
          setAllTags(data.map(tag => ({name: tag.label, label: tag.label})));
      });
    }
  }, [apiTags, isMounted]);

  useEffect(() => {
    if (selectedTags && selectedTags.length > 0) clearErrors("tags");
  }, [selectedTags, clearErrors]);

  const searchTags = useCallback(
    query => {
      if (query) {
        const lower = query.toLowerCase();
        const selectedLabels = (selectedTags || []).map(tag => tag.label?.toLowerCase());
        const tempResults = allTags.filter(
          option =>
            option.label?.toLowerCase().includes(lower) &&
            !selectedLabels.includes(option.label.toLowerCase())
        );
        tempResults.sort(
          (a, b) => a.label.toLowerCase().indexOf(lower) - b.label.toLowerCase().indexOf(lower)
        );
        setTagResults(tempResults);
      } else {
        const selectedLabels = (selectedTags || []).map(tag => tag.label?.toLowerCase());
        setTagResults(
          allTags.filter(option => !selectedLabels.includes(option.label?.toLowerCase()))
        );
      }
    },
    [allTags, selectedTags]
  );

  const handleSave = values => {
    setLoading(true);
    const ids = selected.map(row => row.id);
    const tags = values.tags.map(tag => tag.label);

    api
      .callPut(null, {
        ids,
        tags,
        facilityId,
        userId: currentUser.publicId
      })
      .then(response => {
        if (response?.status === 200) {
          setVisible(false);
          updateTable();
        }
        setLoading(false);
      });
  };

  const count = selected?.length || 0;

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeaderWrapper>
        <ModalTitle>Update Tags</ModalTitle>
        <Help title="Learn more">
          A tag controls which tables a row appears in. Rows show up in every table that includes
          one of their tags, so changing a tag moves the row to tables with that tag.
        </Help>
      </HeaderWrapper>
      <Text>
        Edit tags for <strong>{count}</strong> selected row{count === 1 ? "" : "s"}. The tags below
        will be applied to all of them.
      </Text>
      {tagsDiffer && (
        <Text>
          Selected rows have different tags. Shared tags are shown below — update the list to set
          tags for every selected row.
        </Text>
      )}

      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleSave)} noValidate>
          <FormGroup>
            <FormField>
              <SearchSelect
                label="Tags"
                placeholder="Tag..."
                results={tagResults}
                setResults={setTagResults}
                search={searchTags}
                add={result => {
                  if (result.name) appendTag(result);
                  else appendTag({name: result, label: result});
                }}
                allowNew
              />
              {selectedTags && selectedTags.length > 0 && (
                <Selections>
                  {selectedTags.map(({name, label}) => (
                    <Selection
                      type="button"
                      key={name}
                      onClick={() => {
                        setValue(
                          "tags",
                          selectedTags.filter(tag => tag.label !== label)
                        );
                      }}>
                      {label}&nbsp;
                      <FontAwesomeIcon icon={faClose} />
                    </Selection>
                  ))}
                </Selections>
              )}
              <InputError errors={errors} name="tags" />
            </FormField>
          </FormGroup>
          <ButtonFull type="submit" loading={loading ? 1 : 0}>
            {loading ? <ButtonLoader /> : "Update Tags"}
          </ButtonFull>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalBulkTagUpdate.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  facilityId: PropTypes.number.isRequired,
  selected: PropTypes.arrayOf(PropTypes.any),
  updateTable: PropTypes.func.isRequired
};

ModalBulkTagUpdate.defaultProps = {
  selected: []
};

const HeaderWrapper = styled(Inline)`
  width: 100%;
  align-items: center;
  margin-bottom: ${pad}px;
`;

const ModalTitle = styled(HeadingCenter)`
  margin: 0;
`;

const Selections = styled.div`
  position: relative;
  ${flex("row", "wrap", "start", "start")};
`;

const Selection = styled.button`
  display: flex;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.primary};
  border-radius: ${radius};
  padding: ${pad / 2}px;
  width: fit-content;
  margin: ${pad}px ${pad / 2}px 0 0;
  ${voice.quiet};
`;

export default ModalBulkTagUpdate;
